import { useEffect, useRef, useState } from 'react'
import { SettingsType } from '../../constants/enums'
import {
    BROADCASTING_STATE,
    CLASS_BROADCASTER_CONTAINER_OVERRIDE,
    CLASS_BROADCASTER_SETTINGS_VISIBLE,
} from '../../constants/myWebcam'
import {
    getVideoContainer,
    getVideoElement,
    getVideoWrapper,
} from '../../mfcLogic/videoPublisher'
import { resetBroadcastingState } from '../../state/state.app'
import { AppStateType, useAppState } from './useAppState'
import { useSettingSelector } from './useSettings'

export const useMyWebcam = () => {
    const videoContainerRef = useRef<HTMLDivElement>(null)
    const isVideoAppended = useRef(false)
    const isDisconnecting = useRef(false)

    const [containerVideoWrapper, setContainerVideoWrapper] =
        useState<HTMLDivElement>(getVideoWrapper)
    const [containerVideoElement, setContainerVideoElement] =
        useState<HTMLDivElement>(getVideoContainer)

    const [ueoptValue] = useSettingSelector(SettingsType.MY_WEBCAM, 'options')

    const [videoServer, setVideoServer] = useState<string>(null)
    const [showVideoSettings, setShowVideoSettings] = useState(false)
    const [myWebcamUeopt, setMyWebcamUeopt] = useState(ueoptValue)
    const [videoWidth, setVideoWidth] = useState(0)

    const myWebcam = useAppState(AppStateType.MYWEBCAM)
    const { error, publisher, broadcasting } = myWebcam
    const isIdle = broadcasting === BROADCASTING_STATE.OFF
    const isBroadcasting = broadcasting === BROADCASTING_STATE.ON

    useEffect(() => {
        if (!containerVideoWrapper) {
            setContainerVideoWrapper(getVideoWrapper)
        }
        if (!containerVideoElement) {
            setContainerVideoElement(getVideoContainer)
        }

        const appendVideoContainer = () => {
            if (isVideoAppended.current) return
            if (!videoContainerRef.current || !containerVideoElement) return
            try {
                const { classList } = containerVideoElement
                const classArr: string[] = Object.values(
                    JSON.parse(JSON.stringify(classList)),
                )
                containerVideoElement.classList.remove(...classArr)
            } catch (e) {
                console.warn('Error removing video container classes', e)
            }
            try {
                const containerClasses = [CLASS_BROADCASTER_CONTAINER_OVERRIDE]
                if (showVideoSettings) {
                    containerClasses.push(CLASS_BROADCASTER_SETTINGS_VISIBLE)
                }
                containerVideoElement.classList.add(...containerClasses)
                videoContainerRef.current.appendChild(containerVideoElement)
                containerVideoElement.style.display = 'flex'
                isVideoAppended.current = true
            } catch (e) {
                console.warn('Error appending video container', e)
            }
        }

        const removeVideoContainer = () => {
            if (!isVideoAppended.current) return
            if (!containerVideoWrapper || !containerVideoElement) return
            try {
                containerVideoElement.classList.remove(
                    CLASS_BROADCASTER_CONTAINER_OVERRIDE,
                    CLASS_BROADCASTER_SETTINGS_VISIBLE,
                )
                containerVideoWrapper.appendChild(containerVideoElement)
                containerVideoElement.style.display = 'none'
                isVideoAppended.current = false
            } catch (e) {
                console.warn('Error removing video container', e)
            }
        }

        appendVideoContainer()

        if (isBroadcasting) {
            if (containerVideoElement) {
                const videoElement = getVideoElement(containerVideoElement)
                if (videoElement) {
                    const { width } = videoElement.getBoundingClientRect()
                    setVideoWidth(width)
                }
            } else {
                console.warn('containerVideoElement is null')
            }
        }

        return () => removeVideoContainer()
    }, [isBroadcasting])

    useEffect(() => {
        if (!isIdle) return
        if (isDisconnecting.current) {
            resetBroadcastingState()
            isDisconnecting.current = false
        }
        if (videoServer) {
            publisher?.connect()
            setVideoServer(null)
        }
    }, [isIdle, videoServer, publisher])

    useEffect(() => {
        if (myWebcamUeopt !== ueoptValue) {
            console.log(`[UserBroadcaster] Disconnect...`)
            publisher?.disconnect()
            isDisconnecting.current = true
            setMyWebcamUeopt(ueoptValue)
        }
    }, [ueoptValue, publisher])

    return {
        containerVideoElement,
        error,
        isBroadcasting,
        isDisconnecting,
        publisher,
        showVideoSettings,
        videoContainerRef,
        videoWidth,
        setContainerVideoElement,
        setShowVideoSettings,
        setVideoServer,
    }
}
